import React from 'react';
import { AlertCircle, AlertTriangle, Info, X } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

export type BannerType = 'downgrade-scheduled' | 'over-quota' | 'trial-expired' | 'read-only';

interface StatusBannerProps {
  type: BannerType;
  data?: any;
  onDismiss?: () => void;
  onCancel?: () => void;
}

export const StatusBanner: React.FC<StatusBannerProps> = ({ type, data, onDismiss, onCancel }) => {
  const navigate = useNavigate();

  // Configuration par type de bannière
  const configs = {
    'downgrade-scheduled': {
      icon: Info,
      bgColor: 'bg-blue-50',
      borderColor: 'border-blue-400',
      textColor: 'text-blue-800',
      iconColor: 'text-blue-600',
      buttonColor: 'bg-blue-600 hover:bg-blue-700',
    },
    'over-quota': {
      icon: AlertTriangle,
      bgColor: 'bg-orange-50',
      borderColor: 'border-orange-400',
      textColor: 'text-orange-900',
      iconColor: 'text-orange-600',
      buttonColor: 'bg-orange-600 hover:bg-orange-700',
    },
    'trial-expired': {
      icon: AlertCircle,
      bgColor: 'bg-red-50',
      borderColor: 'border-red-400',
      textColor: 'text-red-900',
      iconColor: 'text-red-600',
      buttonColor: 'bg-red-600 hover:bg-red-700',
    },
    'read-only': {
      icon: AlertCircle,
      bgColor: 'bg-gray-50',
      borderColor: 'border-gray-400',
      textColor: 'text-gray-900',
      iconColor: 'text-gray-600',
      buttonColor: 'bg-gray-600 hover:bg-gray-700',
    },
  };

  const config = configs[type];
  const Icon = config.icon;

  // Rendu selon le type
  const renderContent = () => {
    switch (type) {
      case 'downgrade-scheduled':
        return (
          <>
            <div className="flex-1">
              <p className={`font-medium ${config.textColor}`}>
                Votre abonnement passera au plan <strong>{data?.target_plan?.toUpperCase()}</strong> le{' '}
                <strong>{data?.effective_date}</strong>.
              </p>
              {onCancel && (
                <button
                  onClick={onCancel}
                  className="text-sm text-blue-700 underline hover:no-underline mt-1 font-medium"
                >
                  Annuler ce changement
                </button>
              )}
            </div>
          </>
        );

      case 'over-quota':
        return (
          <>
            <div className="flex-1">
              <p className={`font-medium ${config.textColor} mb-2`}>
                Vous dépassez les limites du plan <strong>{data?.current_plan?.toUpperCase()}</strong>
              </p>
              <div className={`flex flex-wrap gap-4 text-sm ${config.textColor}`}>
                {data?.overages?.map((overage: any, idx: number) => (
                  <span key={idx} className="font-medium">
                    {overage.resource === 'projects' && 'Projets'}
                    {overage.resource === 'architectures' && 'Architectures'}
                    {overage.resource === 'deployments' && 'Déploiements'}
                    : <span className="text-red-700">{overage.current}</span>/{overage.limit}
                  </span>
                ))}
              </div>
              <p className={`text-sm mt-2 ${config.textColor}`}>
                {data?.overages?.some((o: any) => o.resource === 'projects') && !data?.overages?.some((o: any) => o.resource === 'architectures')
                  ? 'La création de nouveaux projets est bloquée. Vous pouvez toujours créer des architectures dans vos projets existants.'
                  : 'La création est temporairement bloquée. Supprimez des éléments ou changez de plan.'
                }
              </p>
            </div>
            <div className="flex gap-2 ml-4">
              <button
                onClick={() => navigate('/projects')}
                className="px-4 py-2 bg-white border border-gray-300 rounded-md text-sm font-medium text-gray-900 hover:bg-gray-50"
              >
                Voir l'utilisation
              </button>
              <button
                onClick={() => navigate('/settings/subscription')}
                className={`px-4 py-2 ${config.buttonColor} text-white rounded-md text-sm font-medium`}
              >
                Changer de plan
              </button>
            </div>
          </>
        );

      case 'trial-expired':
        return (
          <>
            <div className="flex-1">
              <p className={`font-medium ${config.textColor} mb-1`}>
                Votre période d'essai est expirée. Choisissez un plan pour continuer à utiliser GenIaC.
              </p>
              <p className={`text-sm ${config.textColor}`}>
                Vos données restent accessibles en lecture. Les actions de création et de déploiement sont désactivées.
              </p>
            </div>
            <button
              onClick={() => navigate('/settings/subscription')}
              className={`px-4 py-2 ${config.buttonColor} text-white rounded-md text-sm font-medium ml-4`}
            >
              Choisir un plan
            </button>
          </>
        );

      case 'read-only':
        return (
          <>
            <div className="flex-1">
              <p className={`font-medium ${config.textColor} mb-1`}>
                Votre organisation est en mode lecture seule.
              </p>
              <p className={`text-sm ${config.textColor}`}>
                Les actions de création et de déploiement sont désactivées. Contactez le support ou mettez à jour votre abonnement.
              </p>
            </div>
            <div className="flex gap-2 ml-4">
              <a
                href="mailto:support@geniac.io"
                className="px-4 py-2 bg-white border border-gray-300 text-gray-900 rounded-md text-sm font-medium hover:bg-gray-50"
              >
                Contacter le support
              </a>
              <button
                onClick={() => navigate('/settings/subscription')}
                className={`px-4 py-2 ${config.buttonColor} text-white rounded-md text-sm font-medium`}
              >
                Mettre à jour l'abonnement
              </button>
            </div>
          </>
        );

      default:
        return null;
    }
  };

  return (
    <div
      className={`${config.bgColor} ${config.borderColor} border-l-4 p-4 mb-6 rounded-r-lg shadow-sm`}
      role="alert"
    >
      <div className="flex items-start">
        <Icon className={`${config.iconColor} h-5 w-5 mr-3 flex-shrink-0 mt-0.5`} />
        {renderContent()}
        {onDismiss && (
          <button
            onClick={onDismiss}
            className={`${config.textColor} hover:opacity-70 ml-4 flex-shrink-0`}
            aria-label="Fermer"
          >
            <X className="h-5 w-5" />
          </button>
        )}
      </div>
    </div>
  );
};

export default StatusBanner;
